import { useQuery } from '@tanstack/react-query';

import api from 'src/axios';
import { RetryQueryAlert } from 'src/components/alerts/RetryQueryAlert';
import { Card } from 'src/components/ui/card';
import { ForecastChart } from 'src/features/forecast/ForecastChart';
import { ForecastLegend } from 'src/features/forecast/ForecastLegend';
import { ForecastPaginatedActionsContainer } from 'src/features/forecastPaginated/forecastPaginatedActions/ForecastPaginatedActionsContainer';
import { ForecastPaginatedControlsContainer } from 'src/features/forecastPaginated/forecastPaginatedControls/ForecastPaginatedControlsContainer';
import { ForecastPaginatedProductDetailsContainer } from 'src/features/forecastPaginated/forecastPaginatedDetails/ForecastPaginatedProductDetailsContainer';
import { ForecastPaginatedTableContainer } from 'src/features/forecastPaginated/forecastPaginatedTable/ForecastPaginatedTableContainer';
import { useForecastPaginated } from 'src/features/forecastPaginated/hooks/useForecastPaginated';
import { useForecastPaginatedControls } from 'src/features/forecastPaginated/hooks/useForecastPaginatedControls';
import {
  getSessionForecastDataQueryKey,
  QUERY_KEY_OPEN_SESSION_FORECAST_DATA,
  QUERY_KEY_SKU_DETAIL,
} from 'src/keys';
import type { SkuDetailData } from 'src/services/api/getSkuDetail';
import forecastService from 'src/services/forecast/forecastService';

export function ForecastPaginated() {
  const {
    page,
    setPage,
    data,
    queryData,
    sku,
    validatedAt,
    validatedNumber,
    totalProducts,
    hasPrevious,
    hasNext,
    sessionId,
    isLoading,
    error,
  } = useForecastPaginated();

  const { data: skuDetail } = useQuery<SkuDetailData>({
    queryKey: [QUERY_KEY_SKU_DETAIL, sku],
    queryFn: async () => {
      const { data } = await api.get(`/products/${sku}`);
      return forecastService.formatSkuDetail(data);
    },
    enabled: !!sku,
  });

  const { handleReset } = useForecastPaginatedControls(
    sessionId ?? '',
    sku ?? '',
    data,
    queryData,
  );

  return (
    <>
      {error ? (
        <div className='flex items-center justify-center p-4'>
          <RetryQueryAlert
            isLoading={isLoading}
            queryKey={
              sessionId
                ? getSessionForecastDataQueryKey(sessionId, page)
                : [QUERY_KEY_OPEN_SESSION_FORECAST_DATA, page]
            }
          />
        </div>
      ) : (
        <div className='flex h-[calc(100vh-65px)] w-full flex-col overflow-y-auto overflow-x-hidden p-6'>
          <ForecastPaginatedActionsContainer
            handleNext={() => setPage(page + 1)}
            handlePrevious={() => setPage(page - 1)}
            handleReset={handleReset}
            hasNext={hasNext}
            hasPrevious={hasPrevious}
            isDestocking={skuDetail?.is_destocking}
            isLoading={isLoading}
            sessionId={sessionId}
            sku={sku}
            totalProducts={totalProducts}
            validatedAt={validatedAt}
            validatedNumber={validatedNumber}
          />
          <div className='mt-4 flex w-full flex-col items-center justify-between lg:flex-row'>
            <ForecastPaginatedProductDetailsContainer
              sku={sku}
              validatedAt={validatedAt}
            />
            <ForecastPaginatedControlsContainer
              data={data}
              isLoading={isLoading}
              sessionId={sessionId}
              sku={sku}
            />
          </div>
          <Card className='relative mt-4 w-full'>
            <ForecastChart
              className='z-10 mt-4 mb-6 -ml-12 h-[32rem] w-[115%] md:mb-0 md:w-[110%] lg:-mb-4 lg:-ml-16'
              data={data || []}
              isNovelty={skuDetail?.is_novelty}
            />
            <ForecastLegend className='right-4 bottom-4 left-4' />
          </Card>
          <Card className='mt-4'>
            <div className='max-h-fit overflow-auto'>
              <ForecastPaginatedTableContainer
                data={data || []}
                isLoading={isLoading}
                sessionId={sessionId}
                sku={sku}
              />
            </div>
          </Card>
        </div>
      )}
    </>
  );
}
